// The headshot editor: drag to reposition, slider to zoom, out comes a 4:5 JPEG.
//
// Hand-rolled rather than pulled from npm, and the reason is the shape of the
// job: one fixed aspect ratio, pan and zoom, no rotation, no filters. That is a
// couple of hundred lines against a dependency this app would carry into every
// bundle — and the geometry lives in common/imageCrop.ts either way, where it
// is unit-tested.
//
// The frame is what the visitor sees; the crop is held in SOURCE pixels, so
// zooming and dragging cannot drift from what gets exported.

import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import i18next from "i18next";
import SubmissionSheet from "./SubmissionSheet";
import { DESKTOP } from "./breakpoints";
import { membershipPrimary } from "./membershipButton";
import {
  autoFrameFor,
  containZoom,
  cropAt,
  croppedFileName,
  exportCrop,
  ICropRect,
  PHOTO_ASPECT,
  PHOTO_MATTE,
  zoomForCrop,
} from "./imageCrop";

// The frame's on-screen size. Both dimensions are set from these, so the box is
// exactly 4:5 whatever the sheet around it is doing.
const FRAME_WIDTH = 260;
const FRAME_WIDTH_DESKTOP = 240;

const MAX_ZOOM = 3;
const ZOOM_STEP = 0.05;
// One arrow press moves this share of the frame — enough to be useful, small
// enough to be precise.
const NUDGE_FRACTION = 0.04;

interface Props {
  // Object URL or data URL for the file the visitor picked.
  imageUrl: string;
  fileName?: string;
  // Where to open. Passed when the visitor is adjusting a frame that already
  // exists — the auto-frame from the drop — so Edit continues from what they
  // are looking at instead of resetting to the geometric default.
  initialCrop?: ICropRect | null;
  onCancel: () => void;
  // The crop is handed back with the file so the caller can reopen the editor
  // exactly where the visitor left it.
  onConfirm: (file: File, previewUrl: string, crop: ICropRect) => void;
}

const PhotoCropSheet: React.FC<Props> = ({
  imageUrl,
  fileName,
  initialCrop,
  onCancel,
  onConfirm,
}) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ x: number; y: number } | null>(null);

  const [source, setSource] = useState<{ width: number; height: number } | null>(null);
  const [crop, setCrop] = useState<ICropRect | null>(null);
  const [zoom, setZoom] = useState(1);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // Decode outside the DOM tree so the frame never renders a half-loaded image,
  // and so a file the browser cannot decode (a .bmp variant, a truncated
  // download) is caught here rather than exporting an empty canvas.
  useEffect(() => {
    const img = new Image();
    let cancelled = false;
    img.onload = () => {
      if (cancelled) return;
      if (!img.naturalWidth || !img.naturalHeight) {
        setFailed(true);
        return;
      }
      imgRef.current = img;
      setSource({ width: img.naturalWidth, height: img.naturalHeight });
      const opening = initialCrop || autoFrameFor(img);
      setCrop(opening);
      // The slider has to agree with the frame it is showing, or the first
      // touch of it jumps the photo.
      setZoom(zoomForCrop(opening, img.naturalWidth, img.naturalHeight));
    };
    img.onerror = () => {
      if (!cancelled) setFailed(true);
    };
    img.src = imageUrl;
    return () => {
      cancelled = true;
    };
    // Keyed on the image alone, on purpose. `initialCrop` is the frame to OPEN
    // on; re-running the decode because its object identity changed would throw
    // away whatever the visitor has dragged since.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

  const applyZoom = (next: number) => {
    if (!source || !crop) return;
    const centreX = crop.x + crop.width / 2;
    const centreY = crop.y + crop.height / 2;
    setZoom(next);
    setCrop(cropAt(source.width, source.height, next, centreX, centreY));
  };

  // CSS pixels per source pixel, for translating a drag into the crop's space.
  const frameScale = () => {
    const frame = frameRef.current;
    if (!frame || !crop || !crop.width) return 1;
    return frame.clientWidth / crop.width;
  };

  const moveBy = (dxSource: number, dySource: number) => {
    if (!source || !crop) return;
    setCrop(
      cropAt(
        source.width,
        source.height,
        zoom,
        crop.x + crop.width / 2 + dxSource,
        crop.y + crop.height / 2 + dySource
      )
    );
  };

  // React pools synthetic events, so every coordinate is read synchronously
  // before any state update can defer the handler's body.
  const onDragStart = (clientX: number, clientY: number) => {
    dragRef.current = { x: clientX, y: clientY };
  };

  const onDragMove = (clientX: number, clientY: number) => {
    const from = dragRef.current;
    if (!from) return;
    const scale = frameScale() || 1;
    // Dragging right must move the picture right, which moves the FRAME left.
    moveBy(-(clientX - from.x) / scale, -(clientY - from.y) / scale);
    dragRef.current = { x: clientX, y: clientY };
  };

  const onDragEnd = () => {
    dragRef.current = null;
  };

  const onKeyDown = (e: any) => {
    if (!crop) return;
    const stepX = crop.width * NUDGE_FRACTION;
    const stepY = crop.height * NUDGE_FRACTION;
    if (e.key === "ArrowLeft") moveBy(-stepX, 0);
    else if (e.key === "ArrowRight") moveBy(stepX, 0);
    else if (e.key === "ArrowUp") moveBy(0, -stepY);
    else if (e.key === "ArrowDown") moveBy(0, stepY);
    else if (e.key === "+" || e.key === "=") applyZoom(Math.min(MAX_ZOOM, zoom + 0.2));
    else if (e.key === "-" || e.key === "_") applyZoom(Math.max(minZoom, zoom - 0.2));
    else return;
    e.preventDefault();
  };

  const onUse = async () => {
    const img = imgRef.current;
    if (!img || !crop) return;
    setBusy(true);
    try {
      const file = await exportCrop(img, crop, croppedFileName(fileName));
      // The preview comes from the exported file, not the original, so what the
      // visitor sees afterwards is exactly what was uploaded.
      onConfirm(file, URL.createObjectURL(file), crop);
    } catch (e) {
      setBusy(false);
      setFailed(true);
    }
  };

  // Where the <img> sits inside the frame, expressed as percentages so it holds
  // at every breakpoint without measuring anything. Percentages resolve against
  // different axes — `width`/`left` against the frame's width, `top` against its
  // height — so each one is divided by the crop dimension on its own axis. The
  // frame and the crop are both 4:5, so the scale factor is the same either way
  // and the image never distorts.
  const imageStyle = (): React.CSSProperties => {
    if (!source || !crop || !crop.width || !crop.height) {
      return { visibility: "hidden" };
    }
    return {
      position: "absolute",
      width: `${(source.width / crop.width) * 100}%`,
      left: `${(-crop.x / crop.width) * 100}%`,
      top: `${(-crop.y / crop.height) * 100}%`,
      maxWidth: "none",
      userSelect: "none",
      pointerEvents: "none",
    };
  };

  // Zooming out past 1 shows the whole photo with the matte around it, which is
  // the only way a landscape photo gets any vertical movement — at fill zoom the
  // frame is already exactly as tall as the photo.
  const minZoom = source ? containZoom(source.width, source.height) : 1;
  const zoomable = !!source && !!crop;

  return (
    <SubmissionSheet
      title={i18next.t("Frame your headshot")}
      onDismiss={busy ? undefined : onCancel}
      /* Framing is a drag, and a drag that ends over the dimmed page used to
         read as a click outside — the sheet closed and took the crop with it. */
      dismissOnScrim={false}
    >
      {failed ? (
        <HelpText>
          {i18next.t(
            "We couldn't open that image. Please try a JPG, PNG, GIF or BMP file."
          )}
        </HelpText>
      ) : (
        <>
          <HelpText>
            {i18next.t(
              "Drag to reposition, zoom to fit more in. Casting sees this crop, nothing else."
            )}
          </HelpText>
          <Frame
            innerRef={(el: HTMLDivElement) => {
              frameRef.current = el;
            }}
            tabIndex={0}
            role="img"
            aria-label={i18next.t("Headshot crop area — use the arrow keys to reposition")}
            onKeyDown={onKeyDown}
            onMouseDown={(e: any) => {
              e.preventDefault();
              onDragStart(e.clientX, e.clientY);
            }}
            onMouseMove={(e: any) => onDragMove(e.clientX, e.clientY)}
            onMouseUp={onDragEnd}
            onMouseLeave={onDragEnd}
            onTouchStart={(e: any) => {
              const t = e.touches[0];
              if (t) onDragStart(t.clientX, t.clientY);
            }}
            onTouchMove={(e: any) => {
              const t = e.touches[0];
              if (!t) return;
              // Otherwise the sheet scrolls under the finger instead of the
              // photo moving.
              e.preventDefault();
              onDragMove(t.clientX, t.clientY);
            }}
            onTouchEnd={onDragEnd}
          >
            {source ? (
              <img src={imageUrl} alt="" style={imageStyle()} draggable={false} />
            ) : (
              <FrameLoading aria-busy="true" />
            )}
          </Frame>

          <ZoomRow>
            <ZoomLabel htmlFor="headshot-zoom">{i18next.t("Zoom")}</ZoomLabel>
            <ZoomInput
              id="headshot-zoom"
              type="range"
              min={minZoom}
              max={MAX_ZOOM}
              step={ZOOM_STEP}
              value={zoom}
              disabled={!zoomable}
              onChange={(e: any) => applyZoom(parseFloat(e.target.value))}
            />
          </ZoomRow>

          <PrimaryButton onClick={onUse} disabled={!crop || busy}>
            {busy ? i18next.t("One moment…") : i18next.t("Use this photo")}
          </PrimaryButton>
        </>
      )}
      <TextButton onClick={onCancel} disabled={busy}>
        {i18next.t("Choose a different photo")}
      </TextButton>
    </SubmissionSheet>
  );
};

const HelpText = styled.p`
  font-size: 14px;
  color: #53627c;
  line-height: 1.5;
  margin: 0 0 4px;
`;

// 4:5 by construction: padding-top on the ratio box, so the frame is exactly
// the shape of the file that leaves here.
const Frame = styled.div`
  position: relative;
  /* Explicit pixels, and NOT the padding-top percentage trick.
     Percentage padding resolves against the CONTAINING BLOCK's width — the
     sheet's content box — not against this element's own width. With a
     max-width of 260 inside a 344-wide sheet, padding-top:125% produced a
     260x430 box instead of 260x325, so the frame showed about 40% more of the
     photo vertically than the export took: you framed one thing and got
     another. The whole crop-to-CSS mapping assumes height === width * 1.25, so
     that ratio has to be a fact, not an approximation. */
  width: 100%;
  max-width: ${FRAME_WIDTH}px;
  aspect-ratio: 4 / 5;
  margin: 0 auto;
  overflow: hidden;

  /* Where aspect-ratio is missing, an explicit height holds the same ratio at
     the width this frame renders at on any real viewport. */
  @supports not (aspect-ratio: 4 / 5) {
    height: ${FRAME_WIDTH / PHOTO_ASPECT}px;
  }
  border-radius: 8px;
  /* The same grey as the empty dropzone, and the same grey the export paints:
     what shows around a zoomed-out photo here is what casting will see. */
  background: ${PHOTO_MATTE};
  cursor: move;
  touch-action: none;
  outline: none;

  &:focus {
    box-shadow: 0 0 0 3px rgba(15, 155, 240, 0.5);
  }

  @media all and (min-width: ${DESKTOP}) {
    max-width: ${FRAME_WIDTH_DESKTOP}px;

    @supports not (aspect-ratio: 4 / 5) {
      height: ${FRAME_WIDTH_DESKTOP / PHOTO_ASPECT}px;
    }
  }
`;

const FrameLoading = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, #e8ecf2 0%, ${PHOTO_MATTE} 100%);
`;

const ZoomRow = styled.div`
  display: flex;
  align-items: center;
  max-width: 260px;
  margin: 0 auto;
  width: 100%;
`;

/* Margin rather than the row's gap: this one has to hold in Safari below 14.1
   as well, and a label touching its slider looks broken rather than tight. */
const ZoomLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #53627c;
  margin-right: 10px;
`;

const ZoomInput = styled.input`
  flex: 1;
  min-width: 0;
`;

const PrimaryButton = styled.button`
  width: 100%;
  flex: none;
  ${membershipPrimary}
  padding: 14px 0;
  min-height: 48px;
`;

const TextButton = styled.button`
  display: block;
  width: 100%;
  flex: none;
  background: none;
  border: none;
  color: #0f9bf0;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 0;
  cursor: pointer;
  min-height: 44px;

  &:disabled {
    color: #8a94a6;
    cursor: default;
  }
`;

export default PhotoCropSheet;
